import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import PatientSearch from 'components/Patient/PatientSearch';
import Icon from 'components/Utils/NewIcon';
import PatientUtils from 'services/PatientUtils';
import { DefaultList as List } from 'services/UIUtils';
import ProgressMessage from 'components/Utils/ProgressMessage';
import SelectableListItem from 'components/Utils/SelectableListItem';
import DefaultButton from 'components/Utils/NewDefaultButton';
import DocumentBadge from 'components/HealthRecords/Documents/DocumentBadge';
import './style.scss';
import LargeResourcePreview from 'components/Utils/LargeResourcePreview/index';
import DocumentDetails from 'components/HealthRecords/Documents/DocumentDetails';
import DocumentUtils from 'services/DocumentUtils';
import { UncontrolledCollapse } from 'components/Utils/Collapse';
import PerformanceUtils from 'services/PerformanceUtils';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import { ReferralActions } from 'scenes/HomeStaff/ReferralsDirectory';
import { useAppSelector } from 'careand-redux/store';

const DEFAULT_ARRAY = [];

const DocumentAssign = (props) => {
  const [patient, setPatient] = useState(props.patient);
  const [loading, setLoading] = useState(false);
  const [secondaryDocumentId, setSecondaryDocumentId] = useState(null);

  const patientDocuments =
    useAppSelector((state) => state.documents.byId[patient?.id]) || DEFAULT_ARRAY;

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (props.patient) patientSelection(patient);
  }, [props.patient]);

  async function patientSelection(patient) {
    setPatient(patient);
    setLoading(true);
    setSecondaryDocumentId(null);

    await dispatch(api.Documents.getPatientDocuments({ id: patient.id }), {
      errorTitle: 'Documents',
    });
    setLoading(false);
  }

  function documentSelection(document) {
    setSecondaryDocumentId(document.id);
  }

  const processedDocuments = useMemo(() => {
    return PerformanceUtils.sortBy(patientDocuments);
  }, [patientDocuments]);

  const secondaryDocument = useMemo(() => {
    return processedDocuments.find((doc) => doc.id === secondaryDocumentId);
  }, [processedDocuments, secondaryDocumentId]);

  return (
    <div className="modal document-assign">
      <div className="patient-selection">
        {!patient && (
          <>
            <div className="title">
              <div onClick={props.backButton} className="back-button">
                <Icon src="arrow-left" />
              </div>
              Select Patient
            </div>
            <div className="content">
              <PatientSearch onSelect={patientSelection} />
            </div>
          </>
        )}
        {!!patient && !secondaryDocument && (
          <DocumentSelectionScreen
            {...props}
            documentSelection={documentSelection}
            patient={patient}
            setPatient={setPatient}
            loading={loading}
            documents={processedDocuments}
            backButton={() => (props.patient ? props.backButton() : patientSelection(null))}
          />
        )}
        {!!patient && !!secondaryDocument && (
          <div className="document-details-wrapper">
            <div onClick={() => setSecondaryDocumentId(null)} className="back-button">
              <Icon src="arrow-left" />
            </div>
            {secondaryDocument?.document_type !== DocumentUtils.DOCUMENT_TYPE.referral && (
              <DocumentDetails document={secondaryDocument} disableEdit={true} />
            )}
            {secondaryDocument?.document_type === DocumentUtils.DOCUMENT_TYPE.referral && (
              <ReferralActions document={secondaryDocument} disableEdit={true} />
            )}
            <DefaultButton
              name="Link to document"
              className="blue"
              onClick={() => props.onSelect(patient, secondaryDocument)}
              style={{ width: '250px' }}
            />
          </div>
        )}
      </div>
      {secondaryDocument?.document_type !== DocumentUtils.DOCUMENT_TYPE.referral && (
        <DocumentPreview {...props} document={secondaryDocument} />
      )}
      <DocumentPreview {...props} document={props.document} />
    </div>
  );
};

const DocumentPreview = (props) => {
  const dispatch = useAppDispatch();

  const id = props.document?.id;
  const title = props.document?.title;

  async function getFile(dispatchOptions) {
    if (id) return dispatch(api.Documents.getDocumentFile({ documentId: id }), dispatchOptions);
  }

  async function getFileInfo() {
    if (id) return dispatch(api.Documents.getDocumentFileInfo({ documentId: id }));
  }

  return (
    <div className="document-preview">
      {!!id && (
        <>
          <span className="small-title truncate">{title}</span>
          <LargeResourcePreview
            key={id}
            autoLoad={true}
            getFile={getFile}
            getDocumentFileInfo={getFileInfo}
          />
        </>
      )}
    </div>
  );
};

const DocumentSelectionScreen = (props) => {
  let { patient = {}, onSelect, loading, documentSelection, documents, backButton } = props;

  const patientId = patient.id;

  const dispatch = useAppDispatch();
  const cachedPatient = useAppSelector((state) => state.patients.byId[patientId]);

  useEffect(() => {
    if (!cachedPatient && patientId) dispatch(api.Patients.getPatient({ id: patientId }));
  }, [cachedPatient, patientId, dispatch]);

  return (
    <>
      <div className="title">
        <div onClick={backButton} className="back-button">
          <Icon src="arrow-left" />
        </div>
        {PatientUtils.getName(patient)}
      </div>
      <div className="content">
        <ProgressMessage show={loading} />
        {!loading && (
          <MyDocumentList
            documentSelection={documentSelection}
            patient={patient}
            onSelect={onSelect}
            documents={documents}
          />
        )}
      </div>
    </>
  );
};

const MyList = ({ documents, documentSelection }) => (
  <List>
    {documents.map((document) => (
      <SelectableListItem
        key={document.id}
        onClick={() => documentSelection(document)}
        item={<DocumentBadge item={document} />}
      />
    ))}
  </List>
);

const MyReferralList = ({ documents, documentSelection }) => (
  <List>
    {documents.map((document) => (
      <SelectableListItem
        key={document.id}
        onClick={() => documentSelection(document)}
        item={<DocumentBadge item={document} />}
      />
    ))}
  </List>
);

const MyDocumentList = (props) => {
  const { documents, patient, onSelect, documentSelection } = props;

  const referrals = useMemo(() => {
    return DocumentUtils.filter(documents, DocumentUtils.DOCUMENT_TYPE.referral);
  }, [documents]);
  const diagnostics = useMemo(() => {
    return DocumentUtils.filter(documents, DocumentUtils.DOCUMENT_TYPE.diagnostic_requisition);
  }, [documents]);

  const others = useMemo(() => {
    return (documents || []).filter(
      ({ document_type }) =>
        document_type !== DocumentUtils.DOCUMENT_TYPE.diagnostic_requisition &&
        document_type !== DocumentUtils.DOCUMENT_TYPE.referral,
    );
  }, [documents]);

  if (documents === null || documents.length === 0)
    return <NotFoundScreen patient={patient} onSelect={onSelect} />;

  return (
    <div className="scroll-list">
      <UncontrolledCollapse title={`Diagnostics (${diagnostics.length})`}>
        <MyList documents={diagnostics} documentSelection={documentSelection} />
      </UncontrolledCollapse>
      <UncontrolledCollapse title={`Referrals (${referrals.length})`}>
        <MyList documents={referrals} documentSelection={documentSelection} />
      </UncontrolledCollapse>
      <UncontrolledCollapse title={`Others (${others.length})`}>
        <MyList documents={others} documentSelection={documentSelection} />
      </UncontrolledCollapse>

      <AssignButton patient={patient} onSelect={onSelect} />
    </div>
  );
};

const AssignButton = (props) => (
  <div className="assign-button">
    <DefaultButton
      name={`Just Assign to ${PatientUtils.getName(props.patient)}`}
      className="blue"
      onClick={() => props.onSelect(props.patient, null)}
      style={{ width: '85%', marginTop: '27px', marginBottom: '27px' }}
    />
  </div>
);

const NotFoundScreen = (props) => (
  <div className="not-found">
    <span>No documents found for {PatientUtils.getName(props.patient)}</span>
    <AssignButton patient={props.patient} onSelect={props.onSelect} />
  </div>
);

DocumentAssign.propTypes = {
  onSelect: PropTypes.func.isRequired,
  document: PropTypes.object.isRequired,
  backButton: PropTypes.func,
  patient: PropTypes.object,
};

export default DocumentAssign;
